import { Check, Gift, ImageIcon, X } from "lucide-react";
import type { CSSProperties } from "react";
import { formatMoney } from "../../lib/format";
import { minorToMajor } from "../../lib/money";
import { resolveAppearance, type Appearance, type Surface } from "./appearance";

const tint = (percent: number): string =>
	`color-mix(in srgb, currentColor ${percent}%, transparent)`;

const surfaceStyle = (surface: Surface): CSSProperties => ({
	background: surface.background,
	color: surface.text,
});

const PREVIEW_SIZES = ["S", "M", "L", "XL"] as const;
const PREVIEW_SELECTED_SIZE = "M";

type AppearancePreviewProps = {
	appearance: Appearance;
	currency: string;
	showGiftPopup: boolean;
};

/**
 * Hand-built likenesses of the storefront's cart widget and gift popup, like
 * the ticker preview: the shop's theme is not here to render the real ones.
 * Colours left to the theme show as a plain white shop, which is what the
 * storefront falls back to.
 */
export const AppearancePreview = ({
	appearance,
	currency,
	showGiftPopup,
}: AppearancePreviewProps) => {
	const { widget, popup } = resolveAppearance(appearance);
	const money = (minor: number) => formatMoney(minorToMajor(minor), currency);

	return (
		<div className="relative overflow-hidden rounded-md border border-line bg-[repeating-linear-gradient(45deg,var(--color-raised)_0px,var(--color-raised)_10px,var(--color-canvas)_10px,var(--color-canvas)_20px)] p-4 pt-8">
			<span className="absolute top-2 left-3 text-[10px] font-medium tracking-wide text-ink-faint uppercase">
				Preview
			</span>

			<div className="grid items-start gap-4 lg:grid-cols-2">
				<div className="space-y-1.5">
					<p className="text-[11px] font-medium text-ink-muted">Progress bar</p>
					<div
						className="rounded-xl border p-3 text-[13px] leading-snug"
						style={{ ...surfaceStyle(widget), borderColor: tint(12) }}
					>
						<p className="font-semibold">Add {money(1200)} to get your 2nd free gift</p>
						<p className="mt-0.5 text-xs font-semibold" style={{ color: widget.accent }}>
							You are saving {money(2999)}
						</p>

						<div className="mt-3 flex items-center">
							<span
								className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
								style={{
									background: widget.accent,
									color: widget.accentInk,
								}}
							>
								<Check className="h-3.5 w-3.5" strokeWidth={3} />
							</span>
							<span
								className="mx-1 block h-1 flex-1 overflow-hidden rounded-full"
								style={{ background: tint(15) }}
							>
								<span
									className="block h-full w-3/5 rounded-full"
									style={{ background: widget.accent }}
								/>
							</span>
							<span
								className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
								style={{
									color: widget.accent,
									boxShadow: `inset 0 0 0 2px ${widget.accent}`,
								}}
							>
								<Gift className="h-3 w-3" strokeWidth={2.5} />
							</span>
							<span
								className="mx-1 block h-1 flex-1 rounded-full"
								style={{ background: tint(15) }}
							/>
							<span
								className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
								style={{ background: tint(12) }}
							>
								<Gift className="h-3 w-3 opacity-50" strokeWidth={2.5} />
							</span>
						</div>
						<div className="mt-1 flex justify-between text-[11px] opacity-70">
							<span>{money(5000)}</span>
							<span>{money(8000)}</span>
							<span>{money(12000)}</span>
						</div>
					</div>
				</div>

				{showGiftPopup ? (
					<div className="space-y-1.5">
						<p className="text-[11px] font-medium text-ink-muted">Gift popup</p>
						<div className="relative rounded-2xl p-4 shadow-lg" style={surfaceStyle(popup)}>
							<X className="absolute top-3 right-3 h-4 w-4 opacity-60" strokeWidth={2} />
							<p className="text-[11px] font-semibold tracking-[0.04em] uppercase opacity-70">
								You unlocked a gift
							</p>
							<p className="mt-0.5 text-[15px] font-semibold">Classic Boxer Brief</p>
							<p className="mt-0.5 flex gap-2 text-[13px]">
								<span className="line-through opacity-60">{money(2999)}</span>
								<span className="font-semibold">Free</span>
							</p>

							<span
								className="mt-3 flex h-20 items-center justify-center rounded-lg"
								style={{ background: tint(8) }}
							>
								<ImageIcon className="h-5 w-5 opacity-40" strokeWidth={1.75} />
							</span>

							<div className="mt-3 flex gap-1.5">
								{PREVIEW_SIZES.map((size) => (
									<span
										key={size}
										className="flex h-7 min-w-9 items-center justify-center rounded-md px-2 text-xs font-medium"
										style={{
											boxShadow: `inset 0 0 0 ${size === PREVIEW_SELECTED_SIZE ? 2 : 1}px ${
												size === PREVIEW_SELECTED_SIZE ? "currentColor" : tint(30)
											}`,
										}}
									>
										{size}
									</span>
								))}
							</div>

							<span
								className="mt-3 block rounded-lg py-2.5 text-center text-[13px] font-semibold"
								style={{
									background: popup.accent,
									color: popup.accentInk,
								}}
							>
								Add to cart
							</span>
							<span className="mt-2 block text-center text-xs underline opacity-70">No thanks</span>
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
};
